/*
 * ddd-product.js — site-wide membership UI for the Shopify theme.
 *
 * Loaded on every page from layout/theme.liquid. Two responsibilities:
 *   1. Header account state (runs everywhere): repoint the Shopify
 *      customer-account icon to OUR membership system, and when signed
 *      in show a "P" initial badge with a sign-out menu instead of the
 *      generic silhouette.
 *   2. Product-page buy buttons (runs only on /products/...): the
 *      membership matrix below.
 *
 * Source of truth is this repo: shopify-theme/assets/ddd-product.js.
 *
 * What it does on a product page:
 *   - Reads the DDD id from the product handle (handles end with
 *     "-dddNNNNNN", e.g. /products/...-ddd004489).
 *   - Reads the stored Supabase session (ddd_session_v1) and asks the
 *     backend /apps/dpp/status who the member is + how many downloads
 *     they have left + whether bulk is enabled.
 *   - Rewrites the buy area per the membership matrix:
 *       signed out          -> "Sign up free to download today" (links to
 *                               the membership page) ABOVE the normal buy
 *                               buttons, which stay so a non-member can
 *                               still buy a one-off.
 *       member, has credits  -> "Download now" (+ "N of M left this week/
 *                               month"); native buy/cart buttons hidden.
 *       member, out of credits -> "You've used your downloads" + "Upgrade"
 *                               and the buy buttons come back (one-off).
 *
 * Bulk "add to cart" basket for Pro/Enterprise is a later iteration; for
 * now those tiers also get the single "Download now".
 *
 * Vanilla JS, no deps. Reuses window.dddAuth if present (from
 * ddd-membership.js) but does not require it.
 */
(function () {
  "use strict";

  var APP_PROXY_PATH =
    (window.DDD_CONFIG && window.DDD_CONFIG.appProxyPath) || "dpp";
  var SESSION_KEY = "ddd_session_v1";
  var MEMBERSHIP_PAGE = "/pages/membership";
  // Backend (Railway) custom domain — hit directly for heavy ZIP streams
  // so they bypass the Shopify App Proxy's size/timeout limits.
  var DOWNLOAD_BASE =
    (window.DDD_CONFIG && window.DDD_CONFIG.downloadBase) ||
    "https://download.thedigitaldesigndock.com";
  // Auth + status hit the backend directly (CORS), NOT the Shopify
  // /apps/dpp proxy path — ad/privacy blockers block "app proxy" URLs,
  // which broke login for some members. This host is unaffected.
  var AUTH_BASE = DOWNLOAD_BASE + "/membership/auth";
  var IS_PRODUCT_PAGE = window.location.pathname.indexOf("/products/") !== -1;

  function proxyUrl(path) {
    return "/apps/" + APP_PROXY_PATH + path;
  }

  // Session helpers delegate to the shared ddd-auth.js (window.DDDAuth),
  // loaded first in <head>, so the session layer can't drift between this
  // file and ddd-membership.js.
  function getSession() {
    return window.DDDAuth.getSession();
  }

  // Persist a session after login/refresh. This was missing from this file
  // (it existed only in ddd-membership.js), so the account-icon login modal
  // threw a ReferenceError the moment the server replied — which the catch
  // block mislabelled as "Could not reach the server", even on a successful
  // 200. Mirrors ddd-membership.js's implementation, same SESSION_KEY shape
  // that getSession() reads back.
  function setSession(payload) {
    return window.DDDAuth.setSession(payload);
  }

  // DDD id from the product handle. Handles end with "-dddNNNNNN".
  function getDddId() {
    var m = window.location.pathname.match(/(ddd\d{3,})(?:[\/?#]|$)/i);
    return m ? m[1].toUpperCase() : null;
  }

  // Find the Dawn buy-button area. Returns the element we hide for members
  // and anchor our buttons next to. Falls back through a few selectors so
  // a theme tweak doesn't silently break it.
  function findBuyArea() {
    var sels = [
      ".product-form__buttons",
      "product-form .product-form__buttons",
      ".product-form",
      "product-form",
      ".product__info-container .product-form"
    ];
    for (var i = 0; i < sels.length; i++) {
      var el = document.querySelector(sels[i]);
      if (el) return el;
    }
    return null;
  }

  // Also hide the dynamic checkout button (Apple Pay / Shop Pay / Buy it now)
  function dynamicCheckoutEls() {
    return Array.prototype.slice.call(
      document.querySelectorAll(".shopify-payment-button, .product-form__buttons .shopify-payment-button")
    );
  }

  function el(tag, attrs, html) {
    var e = document.createElement(tag);
    if (attrs) for (var k in attrs) e.setAttribute(k, attrs[k]);
    if (html != null) e.innerHTML = html;
    return e;
  }

  var STYLE_ID = "ddd-product-styles";
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement("style");
    s.id = STYLE_ID;
    s.textContent =
      // Inherit the theme font everywhere; sizes bumped to sit naturally
      // alongside the site's own type (which is large/airy).
      ".ddd-pdp{margin:0 0 1.25rem;font-family:inherit;}" +
      ".ddd-pdp__btn{display:flex;align-items:center;justify-content:center;width:100%;text-align:center;padding:1.35rem 1.5rem;border-radius:12px;font-weight:700;font-size:1.25rem;line-height:1.2;letter-spacing:.01em;border:none;cursor:pointer;text-decoration:none;box-sizing:border-box;font-family:inherit;}" +
      ".ddd-pdp__btn--primary{background:#0f172a;color:#fff;}" +
      ".ddd-pdp__btn--primary:hover{background:#1e293b;}" +
      ".ddd-pdp__btn--outline{background:#fff;color:#0f172a;border:1px solid #0f172a;margin-top:.6rem;}" +
      ".ddd-pdp__btn--done{background:#047857;color:#fff;cursor:default;}" +
      ".ddd-pdp__btn:disabled{opacity:.6;cursor:wait;}" +
      // Compact per-card download button on collection/grid pages.
      ".ddd-card-btn{display:block;width:100%;margin:.5rem 0 0;padding:.7rem .9rem;border:none;border-radius:9px;background:#0f172a;color:#fff;font-weight:700;font-size:1rem;cursor:pointer;font-family:inherit;text-align:center;box-sizing:border-box;}" +
      ".ddd-card-btn:hover{background:#1e293b;}" +
      ".ddd-card-btn:disabled{opacity:.6;cursor:wait;}" +
      ".ddd-card-btn--done{background:#047857;}" +
      ".ddd-pdp__meta{font-size:1.05rem;color:#64748b;margin:.7rem 0 0;text-align:center;font-family:inherit;}" +
      ".ddd-pdp__err{font-size:1rem;color:#b91c1c;margin:.7rem 0 0;text-align:center;font-family:inherit;}" +
      ".ddd-pdp__hint{font-size:1.15rem;color:#475569;margin:0 0 .6rem;text-align:center;font-weight:500;font-family:inherit;}";
    document.head.appendChild(s);
  }

  // Member mode is a single class on <html>: html.ddd-member. The CSS
  // that hides buy buttons + dynamic checkout + prices lives in the theme
  // <head> (injected pre-paint so members never see a flash of Apple Pay
  // / Add to cart / price). This toggles it for the final decision:
  //   on  = member with credits (downloads free, no payment artefacts)
  //   off = logged out, or member out of credits (buying is an option)
  // Because the head CSS is class-scoped, this also hides prices on
  // collection/category pages, not just the product page.
  function applyMemberMode(on) {
    document.documentElement.classList.toggle("ddd-member", !!on);
  }

  function mountContainer(buyArea) {
    var existing = document.getElementById("ddd-pdp");
    if (existing) return existing;
    var c = el("div", { id: "ddd-pdp", class: "ddd-pdp" });
    // Insert just before the buy area so membership CTA sits above the
    // native buttons (relevant when we keep the buy buttons visible).
    buyArea.parentNode.insertBefore(c, buyArea);
    return c;
  }

  // Set of dddIds this member has already downloaded (from /status).
  var _downloadedSet = {};

  function triggerDownload(dddId, session, btn) {
    btn.disabled = true;
    var orig = btn.textContent;
    btn.textContent = "Preparing your download…";
    // Hit the backend directly (NOT the Shopify proxy) — most products
    // stream as an on-the-fly ZIP and Shopify's proxy can't relay large
    // streamed responses. A hidden iframe pointed at an attachment
    // response downloads without navigating away.
    var url =
      DOWNLOAD_BASE + "/membership/download" +
      "?dddId=" + encodeURIComponent(dddId) +
      "&token=" + encodeURIComponent(session.accessToken);
    var iframe = document.getElementById("ddd-dl-frame");
    if (!iframe) {
      iframe = el("iframe", { id: "ddd-dl-frame", style: "display:none" });
      document.body.appendChild(iframe);
    }
    iframe.src = url;
    setTimeout(function () {
      btn.disabled = false;
      btn.textContent = orig;
      // Optimistically mark this item downloaded so the UI flips even
      // before the next status fetch confirms it.
      _downloadedSet[dddId] = true;
      refreshMemberMode(); // refresh remaining-count + Downloaded states
    }, 4000);
  }

  // Render the product-page buttons. Only called on product pages; the
  // member-mode class (price/buy hiding) is handled separately so it can
  // apply site-wide.
  function renderProduct(state) {
    var buyArea = _buyArea || findBuyArea();
    if (!buyArea) return;
    _buyArea = buyArea;
    var container = _container || mountContainer(buyArea);
    _container = container;
    container.innerHTML = "";
    injectStyles();

    if (!state.signedIn) {
      container.appendChild(el("p", { class: "ddd-pdp__hint" },
        "Members download this free. Three a week on the house."));
      var join = el("a", { class: "ddd-pdp__btn ddd-pdp__btn--primary", href: MEMBERSHIP_PAGE },
        "Sign up free to download today");
      container.appendChild(join);
      return;
    }

    var plan = state.plan || {};
    var usage = state.usage || {};
    var remaining = typeof usage.remaining === "number" ? usage.remaining : null;
    var unlimited = remaining === -1;
    var interval = plan.quotaInterval === "week" ? "week" : "month";
    var dddId = getDddId();

    if (!dddId) return; // can't identify the product; leave as-is

    var alreadyDownloaded = !!_downloadedSet[dddId];

    if (alreadyDownloaded) {
      // Permanent "Downloaded" state — they already own this. Offer a
      // re-download (doesn't cost another credit; it's already theirs).
      var done = el("div", { class: "ddd-pdp__btn ddd-pdp__btn--done" },
        "✓ Downloaded");
      container.appendChild(done);
      var again = el("button", { type: "button", class: "ddd-pdp__btn ddd-pdp__btn--outline" },
        "Download again");
      again.addEventListener("click", function () {
        triggerDownload(dddId, state.__session, again);
      });
      container.appendChild(again);
      return;
    }

    if (unlimited || (remaining != null && remaining > 0)) {
      var dl = el("button", { type: "button", class: "ddd-pdp__btn ddd-pdp__btn--primary" },
        "Download now");
      dl.addEventListener("click", function () {
        triggerDownload(dddId, state.__session, dl);
      });
      container.appendChild(dl);
      var metaText = unlimited
        ? "Included in your " + (plan.name || "membership") + " plan"
        : remaining + " of " + (plan.monthlyLimit || "") + " downloads left this " + interval;
      container.appendChild(el("p", { class: "ddd-pdp__meta" }, metaText));
      return;
    }

    // Out of credits: upgrade + the buy buttons/price are revealed by
    // applyMemberMode(false) in refreshMemberMode.
    container.appendChild(el("p", { class: "ddd-pdp__hint" },
      "You’ve used your downloads this " + interval + "."));
    var up = el("a", { class: "ddd-pdp__btn ddd-pdp__btn--primary", href: MEMBERSHIP_PAGE },
      "Upgrade for more downloads");
    container.appendChild(up);
    container.appendChild(el("p", { class: "ddd-pdp__meta" },
      "or buy this one on its own below"));
  }

  var _buyArea = null;
  var _container = null;

  // Runs site-wide. Fetches membership status, toggles the member-mode
  // class (drives price/buy hiding on every page), and on product pages
  // renders the Download/Upgrade/Sign-up buttons.
  function refreshMemberMode() {
    var session = getSession();
    var url = AUTH_BASE + "/status";
    if (session && session.accessToken) {
      url += "?token=" + encodeURIComponent(session.accessToken);
    }
    fetch(url, { method: "GET", headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var state = data || {};
        state.__session = session;
        // Merge the server's downloaded list into our local set (keep any
        // optimistic entries from a just-clicked download).
        (state.downloadedDddIds || []).forEach(function (id) {
          if (id) _downloadedSet[String(id).toUpperCase()] = true;
        });
        var plan = state.plan || {};
        var usage = state.usage || {};
        var remaining = typeof usage.remaining === "number" ? usage.remaining : null;
        var hasCredits = state.signedIn && (remaining === -1 || (remaining != null && remaining > 0));
        // Member with credits → hide payment artefacts site-wide.
        // Logged out / out of credits → reveal them (buying is an option).
        applyMemberMode(hasCredits);
        if (IS_PRODUCT_PAGE) renderProduct(state);
        // Collection/search/home grids: put a Download (or Downloaded)
        // button on each card for signed-in members with credits.
        enhanceCollectionCards(state, hasCredits);
        // Pro/Enterprise (bulk_enabled): add-to-basket + a "Download all" ZIP.
        enableBulkUI(state, hasCredits);
      })
      .catch(function () {
        // On failure, fail open: reveal the native buy buttons + price.
        applyMemberMode(false);
      });
  }

  // ---------- Collection / grid cards ----------
  // dddId from a product card's link (handle ends -dddNNNNNN).
  function dddIdFromHref(href) {
    if (!href) return null;
    var m = href.match(/(ddd\d{3,})(?:[\/?#]|$)/i);
    return m ? m[1].toUpperCase() : null;
  }

  function enhanceCollectionCards(state, hasCredits) {
    // Only for signed-in members with credits. (Out-of-credits members
    // see normal prices so they can buy; logged-out see prices too.)
    if (!state.signedIn || !hasCredits) return;
    injectStyles();
    // One button per product TILE. Prefer .card-wrapper; fall back to .card.
    // (Iterating both double-processes; and the inner .card__information is
    // hidden for members by the price-hiding layout, which is why the button
    // never showed — so we attach to the tile itself, below each item.)
    var cards = document.querySelectorAll(".card-wrapper");
    if (!cards.length) cards = document.querySelectorAll(".card");
    Array.prototype.forEach.call(cards, function (card) {
      var link = card.querySelector('a[href*="/products/"]');
      if (!link) return;
      var dddId = dddIdFromHref(link.getAttribute("href"));
      if (!dddId) return;

      var downloaded = !!_downloadedSet[dddId];
      var existing = card.querySelector("[data-ddd-card-btn]");
      if (existing) {
        // Idempotent refresh: flip to Downloaded if needed.
        if (downloaded && existing.getAttribute("data-state") !== "done") {
          existing.textContent = "✓ Downloaded";
          existing.setAttribute("data-state", "done");
          existing.classList.add("ddd-card-btn--done");
        }
        return;
      }

      var btn = el("button", {
        type: "button",
        class: "ddd-card-btn" + (downloaded ? " ddd-card-btn--done" : ""),
        "data-ddd-card-btn": "1",
        "data-state": downloaded ? "done" : "ready",
      }, downloaded ? "✓ Downloaded" : "Download");
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        triggerDownload(dddId, state.__session, btn);
      });
      // Attach to the tile itself so it sits below the item and isn't caught
      // by the member CSS that hides the .card__information / price area.
      card.appendChild(btn);
    });
  }

  // ---------- Bulk download basket (Pro / Enterprise) ----------
  // bulk_enabled members get an "Add to basket" button per tile + a floating
  // bar to ZIP everything in one go via the direct /membership/batch endpoint.
  var BASKET_KEY = "ddd_basket_v1";
  var _bulkSession = null;
  function getBasket() {
    try { return JSON.parse(localStorage.getItem(BASKET_KEY) || "[]") || []; } catch (e) { return []; }
  }
  function setBasket(arr) {
    try { localStorage.setItem(BASKET_KEY, JSON.stringify(arr)); } catch (e) {}
    updateBasketBar();
  }
  function inBasket(id) { return getBasket().indexOf(id) !== -1; }
  function toggleBasket(id) {
    var b = getBasket(); var i = b.indexOf(id);
    if (i === -1) b.push(id); else b.splice(i, 1);
    setBasket(b);
  }
  function basketStyles() {
    if (document.getElementById("ddd-basket-style")) return;
    var st = document.createElement("style"); st.id = "ddd-basket-style";
    st.textContent =
      ".ddd-basket-bar{position:fixed;right:16px;bottom:16px;z-index:10000;background:#0f172a;color:#fff;border-radius:12px;padding:.7rem .95rem;box-shadow:0 12px 40px rgba(15,23,42,.35);font-family:inherit;display:none;align-items:center;gap:.7rem;font-size:1rem;}" +
      ".ddd-basket-bar.is-on{display:flex;}" +
      ".ddd-basket-bar__n{font-weight:800;}" +
      ".ddd-basket-bar button{background:#fff;color:#0f172a;border:none;border-radius:8px;padding:.5rem .85rem;font-weight:700;cursor:pointer;font:inherit;}" +
      ".ddd-basket-bar button:disabled{opacity:.6;cursor:wait;}" +
      ".ddd-basket-bar button.ghost{background:transparent;color:#cbd5e1;text-decoration:underline;padding:.3rem;}" +
      ".ddd-card-basket{display:block;width:100%;margin:.4rem 0 0;padding:.55rem .9rem;border:1px solid #0f172a;border-radius:9px;background:#fff;color:#0f172a;font-weight:700;font-size:.95rem;cursor:pointer;font-family:inherit;text-align:center;box-sizing:border-box;}" +
      ".ddd-card-basket--in{background:#0f172a;color:#fff;}";
    document.head.appendChild(st);
  }
  function ensureBasketBar() {
    var bar = document.getElementById("ddd-basket-bar");
    if (bar) return bar;
    basketStyles();
    bar = el("div", { id: "ddd-basket-bar", class: "ddd-basket-bar" });
    bar.innerHTML =
      '<span class="ddd-basket-bar__n" data-n>0</span><span>in basket</span>' +
      '<button type="button" data-dl>Download all</button>' +
      '<button type="button" class="ghost" data-clear>Clear</button>';
    document.body.appendChild(bar);
    bar.querySelector("[data-dl]").addEventListener("click", function () { triggerBatchDownload(); });
    bar.querySelector("[data-clear]").addEventListener("click", function () { setBasket([]); refreshBasketButtons(); });
    return bar;
  }
  function updateBasketBar() {
    var bar = document.getElementById("ddd-basket-bar"); if (!bar) return;
    var n = getBasket().length;
    bar.querySelector("[data-n]").textContent = n;
    bar.classList.toggle("is-on", n > 0);
  }
  function refreshBasketButtons() {
    Array.prototype.forEach.call(document.querySelectorAll("[data-ddd-basket-btn]"), function (b) {
      var on = inBasket(b.getAttribute("data-ddd-basket-btn"));
      b.textContent = on ? "✓ In basket" : "+ Add to basket";
      b.classList.toggle("ddd-card-basket--in", on);
    });
  }
  function triggerBatchDownload() {
    var ids = getBasket();
    if (!ids.length) return;
    var bar = document.getElementById("ddd-basket-bar");
    var dlBtn = bar && bar.querySelector("[data-dl]");
    if (dlBtn) { dlBtn.disabled = true; dlBtn.textContent = "Preparing ZIP…"; }
    var token = (_bulkSession && _bulkSession.accessToken) || ((getSession() || {}).accessToken);
    fetch(DOWNLOAD_BASE + "/membership/batch", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ dddIds: ids, token: token }),
    })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || !d.success || !d.jobId) throw new Error((d && d.error) || "Could not start the download.");
        pollBatch(d.jobId, token, dlBtn, 0);
      })
      .catch(function (err) {
        if (dlBtn) { dlBtn.disabled = false; dlBtn.textContent = "Download all"; }
        window.alert((err && err.message) || "Could not start the bulk download.");
      });
  }
  function pollBatch(jobId, token, dlBtn, tries) {
    if (tries > 60) {
      if (dlBtn) { dlBtn.disabled = false; dlBtn.textContent = "Download all"; }
      window.alert("Your ZIP is taking a while — give it another go shortly.");
      return;
    }
    fetch(DOWNLOAD_BASE + "/membership/batch-status?jobId=" + encodeURIComponent(jobId) + "&token=" + encodeURIComponent(token), { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (d && d.status === "complete" && d.downloadUrl) {
          if (dlBtn) { dlBtn.disabled = false; dlBtn.textContent = "Download all"; }
          var f = document.getElementById("ddd-dl-frame");
          if (!f) { f = el("iframe", { id: "ddd-dl-frame", style: "display:none" }); document.body.appendChild(f); }
          f.src = d.downloadUrl;
          setBasket([]); refreshBasketButtons();
          return;
        }
        if (d && (d.status === "failed" || d.status === "error")) {
          if (dlBtn) { dlBtn.disabled = false; dlBtn.textContent = "Download all"; }
          window.alert("The bulk download failed. Please try again.");
          return;
        }
        setTimeout(function () { pollBatch(jobId, token, dlBtn, tries + 1); }, 2500);
      })
      .catch(function () { setTimeout(function () { pollBatch(jobId, token, dlBtn, tries + 1); }, 2500); });
  }
  function enableBulkUI(state, hasCredits) {
    if (!state.signedIn || !hasCredits) return;
    if (!(state.plan && state.plan.bulkEnabled)) return;
    _bulkSession = state.__session || getSession();
    basketStyles();
    ensureBasketBar();
    updateBasketBar();
    var cards = document.querySelectorAll(".card-wrapper");
    if (!cards.length) cards = document.querySelectorAll(".card");
    Array.prototype.forEach.call(cards, function (card) {
      if (card.querySelector("[data-ddd-basket-btn]")) return;
      var link = card.querySelector('a[href*="/products/"]'); if (!link) return;
      var dddId = dddIdFromHref(link.getAttribute("href")); if (!dddId) return;
      var on = inBasket(dddId);
      var bb = el(
        "button",
        { type: "button", class: "ddd-card-basket" + (on ? " ddd-card-basket--in" : ""), "data-ddd-basket-btn": dddId },
        on ? "✓ In basket" : "+ Add to basket"
      );
      bb.addEventListener("click", function (e) {
        e.preventDefault(); e.stopPropagation();
        toggleBasket(dddId); refreshBasketButtons();
      });
      card.appendChild(bb);
    });
  }

  // ---------- Header account state (runs site-wide) ----------

  // Pull the email out of the Supabase JWT (base64url payload, "email"
  // claim) so we can show the member's initial without an extra request.
  function decodeJwtEmail(token) {
    try {
      var part = token.split(".")[1];
      if (!part) return null;
      part = part.replace(/-/g, "+").replace(/_/g, "/");
      while (part.length % 4) part += "=";
      var decoded = atob(part);
      var json = decodeURIComponent(
        decoded
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      var obj = JSON.parse(json);
      return typeof obj.email === "string" ? obj.email : null;
    } catch (e) {
      return null;
    }
  }

  function ensureHeaderStyles() {
    if (document.getElementById("ddd-header-styles")) return;
    var s = document.createElement("style");
    s.id = "ddd-header-styles";
    s.textContent =
      // Force a true circle: lock BOTH axes (the header icon row uses
      // align-items:stretch which was making the badge tall/oval) and
      // align-self:center so it never stretches vertically.
      ".ddd-acct-badge{box-sizing:border-box!important;display:flex!important;align-items:center!important;justify-content:center!important;width:38px!important;height:38px!important;min-width:38px!important;max-width:38px!important;min-height:38px!important;max-height:38px!important;flex:0 0 38px!important;align-self:center!important;aspect-ratio:1/1!important;padding:0!important;border-radius:50%!important;background:#2f6fa2!important;color:#fff!important;font-family:inherit;font-weight:800!important;font-size:1.05rem!important;line-height:1!important;}" +
      ".ddd-acct-menu{position:fixed;z-index:10000;min-width:230px;background:#fff;color:#0f172a;border:1px solid #e2e8f0;border-radius:12px;box-shadow:0 16px 40px rgba(15,23,42,.18);padding:.6rem;display:none;font-family:inherit;}" +
      ".ddd-acct-menu.is-open{display:block;}" +
      ".ddd-acct-menu__email{font-size:.9rem;color:#94a3b8;padding:.5rem .7rem .6rem;border-bottom:1px solid #f1f5f9;margin-bottom:.4rem;word-break:break-all;}" +
      ".ddd-acct-menu__item{display:block;width:100%;text-align:left;background:none;border:none;padding:.7rem .7rem;border-radius:8px;font-size:1.05rem;color:#0f172a;cursor:pointer;text-decoration:none;box-sizing:border-box;font-family:inherit;}" +
      ".ddd-acct-menu__item:hover{background:#f1f5f9;}" +
      ".ddd-login-modal{position:fixed;inset:0;z-index:10001;background:rgba(15,23,42,.55);display:none;align-items:center;justify-content:center;padding:1rem;font-family:inherit;}" +
      ".ddd-login-modal.is-open{display:flex;}" +
      ".ddd-login-card{position:relative;background:#fff;color:#0f172a;border-radius:14px;max-width:440px;width:100%;padding:2rem 1.75rem 1.5rem;box-shadow:0 24px 64px rgba(15,23,42,.3);}" +
      ".ddd-login-close{position:absolute;top:1rem;right:1rem;background:none;border:none;font-size:1.75rem;line-height:1;cursor:pointer;color:#94a3b8;}" +
      ".ddd-login-title{font-size:1.6rem;font-weight:700;margin:0 0 .35rem;font-family:inherit;}" +
      ".ddd-login-sub{color:#64748b;font-size:1rem;margin:0 0 1.25rem;font-family:inherit;}" +
      ".ddd-login-label{display:block;font-size:.95rem;color:#475569;margin:0 0 .35rem;font-family:inherit;}" +
      ".ddd-login-input{display:block;width:100%;padding:.85rem .9rem;border:1px solid #cbd5e1;border-radius:9px;margin:0 0 1rem;font-size:1.05rem;font-family:inherit;box-sizing:border-box;}" +
      ".ddd-login-input:focus{outline:2px solid #2f6fa2;outline-offset:1px;border-color:#2f6fa2;}" +
      ".ddd-login-btn{display:block;width:100%;padding:1.1rem;background:#0f172a;color:#fff;border:none;border-radius:10px;font-weight:700;font-size:1.15rem;cursor:pointer;font-family:inherit;}" +
      ".ddd-login-btn:disabled{opacity:.6;cursor:wait;}" +
      ".ddd-login-err{color:#b91c1c;font-size:.95rem;margin:0 0 .75rem;display:none;font-family:inherit;}" +
      ".ddd-login-err.is-visible{display:block;}" +
      ".ddd-login-foot{font-size:1rem;color:#64748b;margin:1rem 0 0;text-align:center;font-family:inherit;}" +
      ".ddd-login-foot a{color:#2f6fa2;font-weight:600;text-decoration:underline;}";
    document.head.appendChild(s);
  }

  function closeAcctMenu() {
    var m = document.getElementById("ddd-acct-menu");
    if (m) m.classList.remove("is-open");
  }

  function openAcctMenu(anchor, email) {
    ensureHeaderStyles();
    var m = document.getElementById("ddd-acct-menu");
    if (!m) {
      m = el("div", { id: "ddd-acct-menu", class: "ddd-acct-menu" });
      m.appendChild(el("div", { class: "ddd-acct-menu__email" }, "Signed in as " + (email || "member")));
      var plan = el("a", { class: "ddd-acct-menu__item", href: MEMBERSHIP_PAGE }, "My plan & downloads");
      m.appendChild(plan);
      var out = el("button", { type: "button", class: "ddd-acct-menu__item" }, "Sign out");
      out.addEventListener("click", function () {
        try { localStorage.removeItem(SESSION_KEY); } catch (e) {}
        window.location.href = "/";
      });
      m.appendChild(out);
      document.body.appendChild(m);
      document.addEventListener("click", function (e) {
        if (m.classList.contains("is-open") && !m.contains(e.target) && !anchor.contains(e.target)) {
          closeAcctMenu();
        }
      });
    }
    var r = anchor.getBoundingClientRect();
    m.style.top = Math.round(r.bottom + 8) + "px";
    // Right-align the menu under the icon, clamped to the viewport.
    var left = Math.max(8, Math.round(r.right - 200));
    m.style.left = left + "px";
    m.classList.toggle("is-open");
  }

  // Read the stored session WITHOUT the expiry check (so we can still see
  // the refresh token on an expired session and renew it).
  function getRawSession() {
    return window.DDDAuth.getRawSession();
  }

  // Keep the member signed in past the ~1h access-token lifetime. If the
  // stored token is within 2 min of expiry (or already expired) and we
  // have a refresh token, swap it for a fresh session before anything
  // else reads getSession(). Returns a promise that always resolves.
  function maybeRefreshSession() {
    var s = getRawSession();
    if (!s || !s.refreshToken) return Promise.resolve();
    if (s.accessToken && s.expiresAt && Date.now() < s.expiresAt - 120000) {
      return Promise.resolve(); // still good
    }
    return fetch(AUTH_BASE + "/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ refreshToken: s.refreshToken }),
    })
      .then(function (r) { return r.json().then(function (p) { return { ok: r.ok, p: p }; }); })
      .then(function (res) {
        if (res.ok && res.p && res.p.success) {
          setSession(res.p);
        } else {
          // Refresh failed (token revoked/expired) — clear so the user
          // sees the logged-out state + login option, not a broken one.
          try { localStorage.removeItem(SESSION_KEY); } catch (e) {}
        }
      })
      .catch(function () { /* leave as-is; getSession will handle expiry */ });
  }

  // Compact login modal, available site-wide from the account icon when
  // signed out. Posts to /apps/dpp/login, stores the session, reloads.
  function openLoginModal() {
    ensureHeaderStyles();
    var existing = document.getElementById("ddd-login-modal");
    if (existing) { existing.classList.add("is-open"); return; }
    var modal = el("div", { id: "ddd-login-modal", class: "ddd-login-modal" });
    modal.innerHTML =
      '<div class="ddd-login-card">' +
      '<button type="button" class="ddd-login-close" aria-label="Close">&times;</button>' +
      '<h2 class="ddd-login-title">Sign in</h2>' +
      '<p class="ddd-login-sub">Welcome back to The Digital Design Dock.</p>' +
      '<label class="ddd-login-label">Email</label>' +
      '<input class="ddd-login-input" type="email" name="email" autocomplete="email">' +
      '<label class="ddd-login-label">Password</label>' +
      '<input class="ddd-login-input" type="password" name="password" autocomplete="current-password">' +
      '<p class="ddd-login-err" data-err></p>' +
      '<button type="button" class="ddd-login-btn" data-submit>Sign in</button>' +
      '<p class="ddd-login-foot">New here? <a href="' + MEMBERSHIP_PAGE + '">Sign up free</a> &middot; <a href="/pages/reset">Forgot password?</a></p>' +
      "</div>";
    document.body.appendChild(modal);

    function close() { modal.classList.remove("is-open"); }
    modal.querySelector(".ddd-login-close").addEventListener("click", close);
    modal.addEventListener("click", function (e) { if (e.target === modal) close(); });

    var emailEl = modal.querySelector('input[name="email"]');
    var pwEl = modal.querySelector('input[name="password"]');
    var errEl = modal.querySelector("[data-err]");
    var submit = modal.querySelector("[data-submit]");
    function doLogin() {
      var email = (emailEl.value || "").trim().toLowerCase();
      var password = pwEl.value || "";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || !password) {
        errEl.textContent = "Enter your email and password.";
        errEl.classList.add("is-visible");
        return;
      }
      errEl.classList.remove("is-visible");
      submit.disabled = true;
      submit.textContent = "Signing in…";
      fetch(AUTH_BASE + "/login", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: email, password: password }),
      })
        .then(function (r) { return r.json().then(function (p) { return { ok: r.ok, p: p }; }); })
        .then(function (res) {
          if (!res.ok || !res.p || !res.p.success) {
            errEl.textContent = (res.p && res.p.error) || "Sign-in failed.";
            errEl.classList.add("is-visible");
            submit.disabled = false;
            submit.textContent = "Sign in";
            return;
          }
          setSession(res.p);
          window.location.reload();
        })
        .catch(function () {
          errEl.textContent = "Could not reach the server. Try again.";
          errEl.classList.add("is-visible");
          submit.disabled = false;
          submit.textContent = "Sign in";
        });
    }
    submit.addEventListener("click", doLogin);
    pwEl.addEventListener("keydown", function (e) { if (e.key === "Enter") doLogin(); });
    modal.classList.add("is-open");
    setTimeout(function () { emailEl.focus(); }, 50);
  }

  function setupHeaderAccount() {
    // Collect EVERY account entry point: the desktop header icon AND the
    // mobile menu-drawer account link (Shopify renders both; the desktop
    // one is .small-hide). Previously only the desktop icon opened the
    // login modal, so on mobile you landed on the membership page and had
    // to fake a signup + switch tabs. Now all of them open Log in.
    var links = [];
    var icon = document.querySelector("a.header__icon--account");
    if (icon) links.push(icon);
    Array.prototype.forEach.call(
      document.querySelectorAll('a[href*="customer_authentication"], a[href$="/account"], a[href*="/account/login"], a[href*="/account?"]'),
      function (a) { if (links.indexOf(a) === -1) links.push(a); }
    );

    var session = getSession();

    if (!session || !session.accessToken) {
      // Logged out: every account link opens the Log in modal (href
      // fallback to the membership page if JS is blocked).
      links.forEach(function (a) {
        a.setAttribute("href", MEMBERSHIP_PAGE);
        a.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          openLoginModal();
        });
      });
      return;
    }

    // Logged in: every account link opens the account menu. The desktop
    // icon also gets the initial badge in place of the silhouette.
    var email = decodeJwtEmail(session.accessToken) || (session.user && session.user.email) || "";
    var initial = (email.charAt(0) || "M").toUpperCase();
    ensureHeaderStyles();
    links.forEach(function (a) {
      a.setAttribute("href", MEMBERSHIP_PAGE);
      a.setAttribute("title", "Signed in" + (email ? " as " + email : ""));
      a.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        openAcctMenu(a, email);
      });
    });
    if (icon) {
      icon.setAttribute("aria-label", "Your account");
      icon.innerHTML = '<span class="ddd-acct-badge">' + initial + "</span>";
    }
  }

  // ---------- Init ----------
  function init() {
    // Renew the token first (if near expiry) so the rest of init sees a
    // valid session and the member isn't silently logged out after ~1h.
    maybeRefreshSession().then(function () {
      setupHeaderAccount();
      refreshMemberMode();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
